import { Navigate, RouteObject, createBrowserRouter } from 'react-router-dom';
import WrapperRouteComponent from './config';
import Middleware from '@/middleware';
import { PAGE_TITLES } from '@/constants/titles';
import BaseLayout from '@/layouts/base.layout';
import MainLayout from '@/layouts/main.layout';
import AuthLayout from '@/layouts/auth.layout';
import { AuthWrapper } from '@/components/HOC/AuthWrapper';
import { lazyRetry } from '@/utils/lazy-retry';

// Lazy load các components với retry logic
//module
const AccountDetailsPage = lazyRetry(() => import('@/modules/account-management/details'));
const CardDetailsPage = lazyRetry(() => import('@/modules/card/details'));
const CardPage = lazyRetry(() => import(/* webpackChunkName: "card"*/ '@/modules/card'));
const ProviderPage = lazyRetry(
  () => import(/* webpackChunkName: "provider"*/ '@/modules/provider'),
);
const UserManagementPage = lazyRetry(
  () => import(/* webpackChunkName: "user"*/ '@/modules/user-management'),
);
const TransactionPage = lazyRetry(
  () => import(/* webpackChunkName: "transaction"*/ '@/modules/transaction'),
);
const AccountManagementPage = lazyRetry(
  () => import(/* webpackChunkName: "account-management"*/ '@/modules/account-management'),
);
const EventPage = lazyRetry(() => import(/* webpackChunkName: "event"*/ '@/modules/event'));

const AuditPage = lazyRetry(() => import(/* webpackChunkName: "audit"*/ '@/modules/audit'));
const CustomerPage = lazyRetry(
  () => import(/* webpackChunkName: "customer"*/ '@/modules/customer'),
);
const CustomerDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "customer-detail"*/ '@/modules/customer/customer-detail'),
);
const CustomerDetailsPage = lazyRetry(
  () => import(/* webpackChunkName: "customer-details"*/ '@/modules/customer/customer-details'),
);

const CustomerRequestPage = lazyRetry(
  () => import(/* webpackChunkName: "customer-request"*/ '@/modules/customer-request/'),
);

const StablecoinManagementPage = lazyRetry(
  () => import(/* webpackChunkName: "stablecoin-management"*/ '@/modules/stablecoin-management'),
);

const BusinessFinancePage = lazyRetry(
  () => import(/* webpackChunkName: "business-finance"*/ '@/modules/business-finance'),
);
const BusinessFinanceCustomerDetailPage = lazyRetry(
  () => import(/* webpackChunkName: "business-finance-customer-detail"*/ '@/modules/business-finance/customer-detail'),
);
const ShareCardsPage = lazyRetry(() => import('@/modules/share-card'));
const ShareCardDetailPage = lazyRetry(() => import('@/modules/share-card/detail'));
const CounterpartManagementPage = lazyRetry(() => import('@/modules/counterpart-management'));


//Auth
const LoginPage = lazyRetry(() => import(/* webpackChunkName: "login"*/ '@/pages/Login'));

//not-found
const NotFoundPage = lazyRetry(() => import('@/pages/NotFound'));

// Định nghĩa các routes
const routes: RouteObject[] = [
  {
    path: '/',
    element: (
      <AuthWrapper>
        <Middleware mode="private">
          <WrapperRouteComponent title="">
            <MainLayout />
          </WrapperRouteComponent>
        </Middleware>
      </AuthWrapper>
    ),
    children: [
      {
        path: '/',
        element: <Navigate to="/account" replace />,
      },
      {
        path: '/account/:id',
        element: <WrapperRouteComponent title="Virtual account details"><AccountDetailsPage /></WrapperRouteComponent>,
      },
      {
        path: '/card/:id',
        element: <WrapperRouteComponent title="Card details"><CardDetailsPage /></WrapperRouteComponent>,
      },
      {
        path: '/account',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.ACCOUNT}>
            <AccountManagementPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/card',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CARD}>
            <CardPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/transaction',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.TRANSACTION}>
            <TransactionPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/finance',
        element: (
          <WrapperRouteComponent title="Finance">
            <BusinessFinancePage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/finance/customer/:customerId',
        element: (
          <WrapperRouteComponent title="Customer Detail">
            <BusinessFinanceCustomerDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/providers',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.PROVIDER}>
            <ProviderPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/user',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.USER}>
            <UserManagementPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/event',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.EVENT}>
            <EventPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/audit',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.AUDIT}>
            <AuditPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/customer',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CUSTOMER}>
            <CustomerPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/customer/:id/kyc',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.DetailCustomer}>
            <CustomerDetailPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/customer/:id/details',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CustomerDetails}>
            <CustomerDetailsPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/requests',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.CustomerRequest}>
            <CustomerRequestPage />
          </WrapperRouteComponent>
        ),
      },
      {
        path: '/stablecoin-management',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.STABLECOIN_MANAGEMENT}>
            <StablecoinManagementPage />
          </WrapperRouteComponent>
        ),
      },
      // Retired ledger screens redirect old bookmarks to the customer account list.
      ...[
        '/dashboard',
        '/ledger-accounts',
        '/journals',
        '/entries',
        '/balance-containers',
        '/seed-operations',
        '/reconciliation',
      ].map((path) => ({ path, element: <Navigate to="/account" replace /> })),
      {
        path: '/share-cards',
        element: <WrapperRouteComponent title={PAGE_TITLES.SHARE_CARDS}><ShareCardsPage /></WrapperRouteComponent>,
      },
      {
        path: '/share-cards/:id',
        element: <WrapperRouteComponent title={PAGE_TITLES.SHARE_CARDS}><ShareCardDetailPage /></WrapperRouteComponent>,
      },
      {
        path: '/counterparts',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.COUNTERPART_MANAGEMENT}>
            <CounterpartManagementPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '/',
    element: (
      <WrapperRouteComponent title="">
        <Middleware mode="non-login">
          <AuthLayout />
        </Middleware>
      </WrapperRouteComponent>
    ),
    children: [
      {
        path: 'login',
        element: (
          <WrapperRouteComponent title={PAGE_TITLES.LOGIN}>
            <LoginPage />
          </WrapperRouteComponent>
        ),
      },
    ],
  },
  {
    path: '',
    element: (
      <AuthWrapper>
        <WrapperRouteComponent title="">
          <Middleware mode="private">
            <BaseLayout />
          </Middleware>
        </WrapperRouteComponent>
      </AuthWrapper>
    ),
    children: [],
  },
  {
    path: '/not-found',
    element: (
      <WrapperRouteComponent title={PAGE_TITLES.NOT_FOUND}>
        <NotFoundPage />
      </WrapperRouteComponent>
    ),
  },
  {
    path: '*',
    element: <Navigate to="/not-found" replace />,
  },
];

// Export router
export const router = createBrowserRouter(routes);
